import React, { useState, useCallback } from 'react';

const EQKnobs = ({ deck, eq, onChange }) => {
  const deckColor = deck === 'A' ? '#00d4ff' : '#ff6b35';
  
  return (
    <div className={`eq-knobs-section eq-${deck.toLowerCase()}`}>
      {['low', 'mid', 'high'].map((band) => (
        <EQKnob 
          key={band}
          value={eq[band]}
          onChange={(val) => onChange(band, val)}
          band={band === 'high' ? 'HI' : band === 'mid' ? 'MID' : 'LO'}
          deckColor={deckColor}
          deck={deck}
        />
      ))}
    </div>
  );
};

const EQKnob = ({ value, onChange, band, deckColor, deck }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startValue, setStartValue] = useState(0);
  
  const snapValue = (val) => Math.round(Math.max(-12, Math.min(12, val)));
  const valueToRotation = (val) => (val / 12) * 135;
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartY(e.clientY);
    setStartValue(value);
    e.preventDefault();
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const deltaY = startY - e.clientY;
    const newValue = snapValue(startValue + (deltaY / 8));
    onChange(newValue);
  }, [isDragging, startY, startValue, onChange]);
  
  const handleMouseUp = () => setIsDragging(false);
  
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -1 : 1;
    onChange(snapValue(value + delta));
  };
  
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);
  
  const rotation = valueToRotation(value);
  
  return (
    <div className="eq-knob-unit" onWheel={handleWheel}>
      <div className="eq-knob-wrapper" onMouseDown={handleMouseDown}>
        <svg viewBox="0 0 80 80" className="knob-svg">
          {/* Outer notch marks */}
          {[...Array(25)].map((_, i) => {
            const angle = (-135 + (i * 270 / 24)) * Math.PI / 180;
            const isCenter = i === 12;
            const isMajor = i % 6 === 0;
            const innerR = isMajor ? 32 : 34;
            const x1 = 40 + innerR * Math.sin(angle);
            const y1 = 40 - innerR * Math.cos(angle);
            const x2 = 40 + 38 * Math.sin(angle);
            const y2 = 40 - 38 * Math.cos(angle);
            return (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={isCenter ? '#fff' : isMajor ? '#555' : '#333'}
                strokeWidth={isCenter ? 2 : isMajor ? 1.5 : 1}
              />
            );
          })}
          
          {/* Knob body */}
          <defs>
            <radialGradient id={`knobGrad-${deck}-${band}`} cx="35%" cy="35%">
              <stop offset="0%" stopColor="#4a4a5a" />
              <stop offset="50%" stopColor="#2a2a3a" />
              <stop offset="100%" stopColor="#1a1a2a" />
            </radialGradient>
            <filter id={`knobGlow-${deck}-${band}`}>
              <feGaussianBlur stdDeviation="2" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          
          <circle cx="40" cy="40" r="28" fill={`url(#knobGrad-${deck}-${band})`} />
          
          {/* Grip ridges */}
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30 + rotation) * Math.PI / 180;
            const x1 = 40 + 20 * Math.sin(angle);
            const y1 = 40 - 20 * Math.cos(angle);
            const x2 = 40 + 26 * Math.sin(angle);
            const y2 = 40 - 26 * Math.cos(angle);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#555" strokeWidth="2" strokeLinecap="round" />;
          })}
          
          {/* Indicator */}
          <line x1="40" y1="40" x2="40" y2="14" stroke={deckColor} strokeWidth="3" strokeLinecap="round"
            transform={`rotate(${rotation}, 40, 40)`} filter={`url(#knobGlow-${deck}-${band})`}
          />
          
          {/* Center */}
          <circle cx="40" cy="40" r="10" fill="#1a1a2a" />
          <circle cx="40" cy="40" r="5" fill="#0f0f1a" />
        </svg>
      </div>
      <span className="knob-band-label">{band}</span>
      <span className="knob-value-label" style={{ color: deckColor }}>
        {value > 0 ? '+' : ''}{value}
      </span>
    </div>
  );
};

export default EQKnobs;
